/**
 * WHO is being prequalified — the one axis the shared surface varies on.
 *
 * <p>🔴 <b>The subject, never the address.</b> A prequalification surface can be
 * reached from a buyer, from a submission or from a listing, and none of those
 * change what the reader is looking at: either the BUYER's ability to fund a
 * purchase, or the SELLER's ability to carry a note. Parameterising on the
 * record you happened to arrive from is what produced two near-identical
 * implementations in the first place — same rows, same statuses, same clipboard
 * behaviour, differing in a noun.</p>
 *
 * <p>The ADDRESS is carried separately, by the adapter, because that is where
 * the authorization gate lives: a submission-addressed write takes the buyer's
 * own per-row ownership rule, and a listing-addressed one takes
 * <c>Listings.Manage</c> with no row check, which is that controller's own
 * documented rule. Folding the two axes together would let a change of surface
 * silently change who may send.</p>
 */
export type PrequalSubject = "buyer" | "seller";

/**
 * Every word that differs between the two subjects, in one place.
 *
 * <p>Authored as a closed record rather than composed from the noun at each call
 * site: "invite the buyer" and "invite the seller" happen to interpolate
 * cleanly, but "no prequalification is linked" and "no seller prequalification
 * is linked" do not, and a template that mostly works is how one sentence ends
 * up reading like a different product.</p>
 */
export interface PrequalSubjectCopy {
  /** The bare noun. Lower case; the only place it is capitalised is a sentence
   *  start, which the copy below already handles. */
  noun: string;
  /** The surface's own heading. */
  sectionTitle: string;
  /** The invitations block's heading — identical either way today, and kept per
   *  subject so a future divergence has somewhere to go. */
  invitationsTitle: string;
  invitationsCaption: string;
  /** The send button. */
  inviteAction: string;
  /** What "this address already has a live invitation" is about. */
  duplicateSubject: string;
  /** The empty invitation list. */
  invitesEmptyTitle: string;
  invitesEmptyDescription: string;
  /** What a failed read failed to load — reads as "Couldn't load {this}". */
  invitesWhat: string;
  linkWhat: string;
  /** The link-correction controls. */
  linkExistingAction: string;
  changeLinkAction: string;
  /** Nothing is linked yet. */
  unlinkedCopy: string;
}

export const PREQUAL_SUBJECT_COPY: Record<PrequalSubject, PrequalSubjectCopy> = {
  buyer: {
    noun: "buyer",
    sectionTitle: "Prequalification",
    invitationsTitle: "Invitations",
    invitationsCaption: "Invitations to complete the prequalification funnel.",
    inviteAction: "Send invitation",
    duplicateSubject: "This buyer",
    invitesEmptyTitle: "No invitations yet",
    invitesEmptyDescription:
      "Send one to invite this buyer to prequalify, or link a prequalification they already completed.",
    invitesWhat: "the invitations",
    linkWhat: "the linked prequalification",
    linkExistingAction: "Link an existing prequalification",
    changeLinkAction: "Change the linked prequalification",
    unlinkedCopy:
      "No prequalification is linked. A submission package embeds the buyer's Phoenix "
      + "prequalification letter, so one is needed before a package can be generated.",
  },
  seller: {
    noun: "seller",
    sectionTitle: "Seller prequalification",
    invitationsTitle: "Invitations",
    invitationsCaption:
      "Every invitation this seller has been sent — from here and from any buyer's submission.",
    inviteAction: "Invite the seller",
    duplicateSubject: "That address",
    invitesEmptyTitle: "No invitations yet",
    invitesEmptyDescription:
      "Invite the seller to prequalify, or link one they already completed.",
    invitesWhat: "the seller's invitations",
    linkWhat: "the seller prequalification",
    linkExistingAction: "Link an existing one",
    changeLinkAction: "Change",
    // 🔴 Says out loud that escalating without one still WORKS. A seller
    // prequalification depends on a third party Phoenix cannot compel, so the
    // gate warns and proceeds — and a specialist who reads this as a blocker
    // will chase a seller who was never going to answer.
    unlinkedCopy:
      "No seller prequalification is linked. Escalating without one still works — a seller "
      + "prequalification depends on a third party Phoenix cannot compel — but the hand-off "
      + "will carry a warning for the liaison.",
  },
};
